import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, m } from 'motion/react';
import { X } from 'lucide-react';
import {
  daysInMonth,
  formatYmd,
  parseYm,
  type CategoryNode,
  type AccountsResponse,
  type TagsResponse,
  type TransactionType,
  type Ym,
  type Ymd,
} from '@lira/shared';
import { apiGet } from '@/lib/api';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { useBodyScrollLock } from '@/components/ui/useDialog';
import { CategoryIcon, Toggle } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { TxFilters } from './filters';

const EASE = [0.16, 1, 0.3, 1] as const;

const TYPE_CHOICES: { value: TransactionType | null; label: string }[] = [
  { value: null, label: 'Todos' },
  { value: 'expense', label: 'Despesas' },
  { value: 'income', label: 'Receitas' },
  { value: 'transfer', label: 'Transferências' },
];

const STATUS_CHOICES: { value: TxFilters['status']; label: string }[] = [
  { value: null, label: 'Todas' },
  { value: 'effected', label: 'Efetivadas' },
  { value: 'pending', label: 'Pendentes' },
];

function firstOfMonth(month: Ym): Ymd {
  const { year, month: m } = parseYm(month);
  return formatYmd(year, m, 1);
}
function lastOfMonth(month: Ym): Ymd {
  const { year, month: m } = parseYm(month);
  return formatYmd(year, m, daysInMonth(year, m));
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
        active
          ? 'border-primary bg-primary-weak text-primary'
          : 'border-border text-muted hover:border-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-[13px] font-semibold text-ink">{label}</p>
      {children}
    </div>
  );
}

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  value: TxFilters;
  month: Ym;
  onApply: (f: TxFilters) => void;
  groupCards: boolean;
  onGroupCardsChange: (value: boolean) => void;
}

/** Painel de filtros: drawer lateral (desktop) / bottom sheet (mobile). */
export function FilterDrawer({
  open,
  onClose,
  value,
  month,
  onApply,
  groupCards,
  onGroupCardsChange,
}: FilterDrawerProps) {
  const isDesktop = useIsDesktop();
  const [draft, setDraft] = useState<TxFilters>(value);
  const [draftGroupCards, setDraftGroupCards] = useState(groupCards);
  useBodyScrollLock(open);

  useEffect(() => {
    if (open) {
      setDraft(value);
      setDraftGroupCards(groupCards);
    }
  }, [groupCards, open, value]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const categoriesQuery = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: () => apiGet<{ categories: CategoryNode[] }>('/categories'),
    enabled: open,
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts', 'compose'],
    queryFn: () => apiGet<AccountsResponse>('/accounts'),
    enabled: open,
  });
  const tagsQuery = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiGet<TagsResponse>('/tags'),
    enabled: open,
  });

  const categoryRoots = categoriesQuery.data?.categories ?? [];
  const categories = categoryRoots.flatMap((n) => [n, ...n.children]);
  const accounts = (accountsQuery.data?.accounts ?? []).filter((a) => !a.archived);
  const tags = (tagsQuery.data?.tags ?? []).filter((t) => !t.archived);

  const toggle = (arr: string[], id: string): string[] =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const reset = () => {
    setDraft({
      type: null,
      status: null,
      categoryIds: [],
      accountIds: [],
      tagIds: [],
      from: null,
      to: null,
      search: draft.search,
    });
    setDraftGroupCards(false);
  };

  const apply = () => {
    onApply(draft);
    onGroupCardsChange(draftGroupCards);
    onClose();
  };

  const fromValue = draft.from ?? firstOfMonth(month);
  const toValue = draft.to ?? lastOfMonth(month);

  const body = (
    <>
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <h2 className="text-lg font-semibold text-ink">Filtrar</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="grid size-9 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
        >
          <X className="size-5" />
        </button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <Section label="Período">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[12px] text-muted">De</span>
              <input
                type="date"
                value={fromValue}
                max={toValue}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value || null }))}
                className="h-10 w-full rounded-lg border border-field bg-surface px-2.5 text-[14px] text-ink focus:border-primary focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[12px] text-muted">Até</span>
              <input
                type="date"
                value={toValue}
                min={fromValue}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value || null }))}
                className="h-10 w-full rounded-lg border border-field bg-surface px-2.5 text-[14px] text-ink focus:border-primary focus:outline-none"
              />
            </label>
          </div>
        </Section>

        <Section label="Tipo">
          <div className="flex flex-wrap gap-2">
            {TYPE_CHOICES.map((t) => (
              <Chip
                key={t.label}
                active={draft.type === t.value}
                onClick={() => setDraft((d) => ({ ...d, type: t.value }))}
              >
                {t.label}
              </Chip>
            ))}
          </div>
        </Section>

        <Section label="Situação">
          <div className="flex flex-wrap gap-2">
            {STATUS_CHOICES.map((s) => (
              <Chip
                key={s.label}
                active={draft.status === s.value}
                onClick={() => setDraft((d) => ({ ...d, status: s.value }))}
              >
                {s.label}
              </Chip>
            ))}
          </div>
        </Section>

        <Section label="Cartões de crédito">
          <Toggle
            checked={draftGroupCards}
            onChange={setDraftGroupCards}
            accent="cards"
            label="Agrupar cartões"
            className="min-h-11 rounded-lg border border-border px-3"
          />
        </Section>

        <Section label="Contas">
          <div className="flex flex-wrap gap-2">
            {accounts.map((a) => (
              <Chip
                key={a.id}
                active={draft.accountIds.includes(a.id)}
                onClick={() => setDraft((d) => ({ ...d, accountIds: toggle(d.accountIds, a.id) }))}
              >
                <CategoryIcon icon={a.icon} color={a.color} size={18} fallbackText={a.name} />
                {a.name}
              </Chip>
            ))}
            {accounts.length === 0 && <p className="text-[13px] text-muted">Nenhuma conta.</p>}
          </div>
        </Section>

        <Section label="Categorias">
          <label className="sr-only" htmlFor="filter-category">Adicionar categoria ao filtro</label>
          <select
            id="filter-category"
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (id && !draft.categoryIds.includes(id)) setDraft((d) => ({ ...d, categoryIds: [...d.categoryIds, id] }));
            }}
            className="h-10 w-full rounded-lg border border-field bg-surface px-3 text-sm text-ink focus:border-primary focus:outline-none"
          >
            <option value="">Selecionar categoria…</option>
            {categoryRoots.map((parent) => (
              <optgroup key={parent.id} label={parent.name}>
                <option value={parent.id}>{parent.name} (todas)</option>
                {parent.children.map((child) => <option key={child.id} value={child.id}>↳ {child.name}</option>)}
              </optgroup>
            ))}
          </select>
          <div className="mt-2 flex flex-wrap gap-2">
            {draft.categoryIds.map((id) => {
              const c = categories.find((item) => item.id === id);
              if (!c) return null;
              return <Chip key={id} active onClick={() => setDraft((d) => ({ ...d, categoryIds: toggle(d.categoryIds, id) }))}><CategoryIcon icon={c.icon} color={c.color} size={18} />{c.name} ×</Chip>;
            })}
            {categories.length === 0 && <p className="text-[13px] text-muted">Nenhuma categoria.</p>}
          </div>
        </Section>

        <Section label="Tags">
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <Chip
                key={t.id}
                active={draft.tagIds.includes(t.id)}
                onClick={() => setDraft((d) => ({ ...d, tagIds: toggle(d.tagIds, t.id) }))}
              >
                {t.name}
              </Chip>
            ))}
            {tags.length === 0 && <p className="text-[13px] text-muted">Nenhuma tag.</p>}
          </div>
        </Section>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-4">
        <button
          type="button"
          onClick={reset}
          className="text-sm font-semibold text-muted transition-colors hover:text-ink"
        >
          Limpar
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={apply}
            className="h-11 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
          >
            Aplicar filtros
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: EASE }}
          onClick={onClose}
          className={cn('fixed inset-0 z-50 bg-black/40', isDesktop ? 'flex justify-end' : 'flex items-end')}
        >
          <m.aside
            key="panel"
            role="dialog"
            aria-modal="true"
            aria-label="Filtros"
            onClick={(e) => e.stopPropagation()}
            initial={isDesktop ? { x: '100%' } : { y: '100%' }}
            animate={isDesktop ? { x: 0 } : { y: 0 }}
            exit={isDesktop ? { x: '100%' } : { y: '100%' }}
            transition={{ duration: 0.28, ease: EASE }}
            className={cn(
              'flex flex-col bg-surface shadow-[var(--shadow-modal)]',
              isDesktop
                ? 'h-full w-[400px] max-w-full'
                : 'max-h-[90dvh] w-full rounded-t-xl pb-[env(safe-area-inset-bottom)]',
            )}
          >
            {body}
          </m.aside>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
